import React, { useState, useEffect, ReactNode, useRef } from 'react';
import authService from 'src/services/auth';
import useMyProfile from 'src/hooks/useMyProfile';
import { toastError } from 'src/services/toast';
import { getCompanyAccount } from 'src/services/account';
import { getMenu } from 'src/services/menu';
import { getPlans, getSubscriptionDetails } from 'src/services/billing';
import * as amplitude from '@amplitude/analytics-browser';
import UserContext from './UserContext';
import { ICompanyAccount, IUser } from 'src/lib/schemas';
import { IMenuItem } from 'src/services/menu';
import { IPlan, ISubscription } from 'src/models/account/interfaces';
import posthog from 'posthog-js';
import { isEqual } from 'lodash';
import { isProdEnvironment } from 'src/lib/utils';

interface CustomWindow extends Window {
	dataLayer: Array<Record<string, any>>;
	newrelic: any;
}

declare const window: CustomWindow;

interface UserProviderProps {
	children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
	const [user, setUser] = useState<IUser | null>(authService.getCurrentUser());
	const [account, setAccount] = useState<ICompanyAccount | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [navList, setNavList] = useState<IMenuItem[]>([]);
	const [subscription, setSubscription] = useState<ISubscription | null>(null);
	const [plans, setPlans] = useState<IPlan[]>([]);
	const [currentPlan, setCurrentPlan] = useState<IPlan | undefined>(undefined);
	const profile = useMyProfile();
	const subscriptionRef = useRef<ISubscription | null>(null);
	const subscriptionErrorCountRef = useRef<number>(0);
	const subscriptionIntervalRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (!user?.account) return;

		const isInternalUser =
			user.email?.endsWith('fusionads.ai') ||
			user.email?.endsWith('fusionos.ai') ||
			user.email?.endsWith('fusioncloud.ai') ||
			false;

		// Google Tag Manager / DataLayer
		const dataLayerPayload: Record<string, any> = {
			event: 'identify',
			user_id: user.id,
			name: user.name,
			account_id: user.account,
			is_internal_user: isInternalUser,
		};
		if (user.email) {
			dataLayerPayload.email = user.email;
		} else if (user.phone) {
			dataLayerPayload.phone = user.phone;
		}
		window.dataLayer.push(dataLayerPayload);

		// New Relic
		if (window.newrelic && typeof window.newrelic.setUserId === 'function') {
			window.newrelic.setUserId(user.id);
			if (user.email) {
				window.newrelic.setCustomAttribute('email', user.email);
			} else if (user.phone) {
				window.newrelic.setCustomAttribute('phone', user.phone);
			}
			if (isInternalUser) {
				window.newrelic.pauseReplay();
			}
		}

		// PostHog (only in production)
		if (posthog && isProdEnvironment()) {
			const posthogPayload: Record<string, any> = {
				name: user.name,
				account_id: user.account,
				is_internal_user: isInternalUser,
			};
			if (user.email) {
				posthogPayload.email = user.email;
			} else if (user.phone) {
				posthogPayload.phone = user.phone;
			}
			posthog.identify(user.id, posthogPayload);
		}
	}, [user]);

	useEffect(() => {
		amplitude.setUserId(user?.id);
	}, [user]);

	useEffect(() => {
		if (profile.user) {
			setUser(profile.user);
		}
	}, [profile.user]);

	useEffect(() => {
		subscriptionRef.current = subscription;
	}, [subscription]);

	const fetchCompanyAccount = async () => {
		if (!user) return;
		try {
			const data = await getCompanyAccount(user.account);
			setAccount(data);
		} catch (error) {
			toastError(error);
		}
		setIsLoading(false);
	};

	const fetchMenu = async () => {
		try {
			const menuData = await getMenu();
			setNavList(menuData);
		} catch (error) {
			console.error('Error fetching menu:', error);
		}
	};

	const fetchSubscription = async () => {
		if (account?.subscription && authService.isAuthenticated()) {
			try {
				const newSubscription = await getSubscriptionDetails(
					account.subscription._id,
				);

				if (!isEqual(newSubscription, subscriptionRef.current)) {
					setCurrentPlan(
						plans.find((plan) => plan._id === newSubscription.plan.id),
					);
					setSubscription(newSubscription);
				}
				// Reset error count on successful fetch
				subscriptionErrorCountRef.current = 0;
				return newSubscription;
			} catch (error) {
				// Track consecutive errors
				subscriptionErrorCountRef.current += 1;

				// Stop polling after 3 consecutive failures to prevent console spam
				if (subscriptionErrorCountRef.current >= 3) {
					if (subscriptionIntervalRef.current) {
						clearInterval(subscriptionIntervalRef.current);
						subscriptionIntervalRef.current = null;
						console.warn('⚠️ Subscription polling stopped after 3 consecutive failures. Endpoint may not be available.');
					}
				}
			}
		}
	};

	const fetchPlans = async () => {
		try {
			const plansData = await getPlans();
			setPlans(plansData);
		} catch (error) {
			console.error('Error fetching subscription plans:', error);
		}
	};

	useEffect(() => {
		if (user && authService.isAuthenticated()) {
			fetchCompanyAccount();
			fetchMenu();
			fetchPlans();
		}
	}, [user]);

	useEffect(() => {
		if (account?.subscription && authService.isAuthenticated()) {
			setCurrentPlan(
				plans.find(
					(plan) => plan._id === account?.subscription?.internalPlanId,
				),
			);
			// Reset error count when starting new polling
			subscriptionErrorCountRef.current = 0;
			fetchSubscription();
			subscriptionIntervalRef.current = setInterval(() => {
				fetchSubscription();
			}, 10000);
			return () => {
				if (subscriptionIntervalRef.current) {
					clearInterval(subscriptionIntervalRef.current);
					subscriptionIntervalRef.current = null;
				}
			};
		}
	}, [account?.subscription, plans]);

	// Expose account and user to global window for use in tools and scraping
	useEffect(() => {
		(window as any).__fusionAccount = account;
		(window as any).__fusionUser = user;
	}, [account, user]);

	const pendingAccountSettings = () => {
		const pendingSettings = [];
		if (!account?.websiteLink) pendingSettings.push('websiteLink');
		if (!account?.name) pendingSettings.push('name');
		if (!account?.description) pendingSettings.push('description');
		if (!account?.industry) pendingSettings.push('industry');
		if (!account?.keywords || account.keywords.length === 0)
			pendingSettings.push('keywords');
		if (!account?.logo) pendingSettings.push('logo');
		if (!account?.brandIdentity?.fontType) pendingSettings.push('fontType');
		return pendingSettings;
	};

	// Refresh account data (e.g., after copilot updates account settings)
	const refreshAccount = async () => {
		if (!user) return;
		try {
			console.log('[UserProvider] Refreshing account data...');
			const data = await getCompanyAccount(user.account);
			setAccount(data);
			console.log('[UserProvider] Account data refreshed');
		} catch (error) {
			console.error('[UserProvider] Error refreshing account:', error);
		}
	};

	return (
		<UserContext.Provider
			value={{
				user,
				account,
				isLoading,
				navList,
				setUser,
				setAccount,
				setNavList,
				pendingAccountSettings,
				subscription,
				plans,
				currentPlan,
				fetchSubscription,
				refreshAccount,
			}}
		>
			{children}
		</UserContext.Provider>
	);
};
